<script>
import { cloneDeep } from 'lodash-es';
import tripDirections from '../../../variables/tripDirections';

export default {
  name: 'ItineraryPricesPriceIntervalCreator',
  props: {
    priceIntervals: {
      type: Array,
      required: true,
    },
    priceInterval: {
      type: Object,
      default: () => {},
    },
    editDialog: {
      type: Boolean,
      required: true,
    },
    itineraryLength: {
      type: Number,
      required: true,
    },
    editIndex: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      localEditDialog: this.editDialog,
      datePickers: {
        startDate: false,
        bookingFrom: false,
        bookingTo: false,
        arrivalFrom: false,
        arrivalTo: false,
      },
      localPriceInterval: null,
      daysDisabled: false,
      days: [
        { id: 0, day: 'sunday', label: this.$t('nonDynamicTransport.days.sunday') },
        { id: 1, day: 'monday', label: this.$t('nonDynamicTransport.days.monday') },
        { id: 2, day: 'tuesday', label: this.$t('nonDynamicTransport.days.tuesday') },
        { id: 3, day: 'wednesday', label: this.$t('nonDynamicTransport.days.wednesday') },
        { id: 4, day: 'thursday', label: this.$t('nonDynamicTransport.days.thursday') },
        { id: 5, day: 'friday', label: this.$t('nonDynamicTransport.days.friday') },
        { id: 6, day: 'saturday', label: this.$t('nonDynamicTransport.days.saturday') },
      ],
      availableOutboundDays: [],
      outboundDays: [],
      localOutboundDays: [],
      today: this.$moment().format('YYYY-MM-DD'),
    };
  },
  watch: {
    priceInterval: {
      deep: true,
      immediate: true,
      handler(value) {
        this.localPriceInterval = cloneDeep(value);
        this.findAvailableOutboundDays();
        this.outboundDays = [];
        this.localOutboundDays = this.localPriceInterval.intervalDays;
        this.availableOutboundDays.forEach((day) => {
          if (this.localPriceInterval.intervalDays.findIndex(d => d.day === day.id) !== -1) {
            this.outboundDays.push(day);
          }
        });
      },
    },
    editDialog: {
      handler(value) {
        this.localEditDialog = value;
      },
    },
  },
  computed: {
    activeStatus() {
      return this.localPriceInterval.active ? 'Active' : 'Deactivated';
    },
  },
  methods: {
    getValue(value) {
      return typeof value !== 'undefined' ? value : null;
    },
    setStartDate(value) {
      const startDate = this.getValue(value);
      this.localPriceInterval.startDate = startDate;
      if (startDate) {
        this.daysDisabled = true;
        this.localOutboundDays = [];
        this.localPriceInterval.arrivalFrom = startDate;
        this.localPriceInterval.arrivalTo = this.$moment(startDate, 'YYYY-MM-DD')
          .add(this.itineraryLength, 'days')
          .format('YYYY-MM-DD');
      } else {
        this.daysDisabled = false;
        this.localPriceInterval.arrivalFrom = startDate;
        this.localPriceInterval.arrivalTo = startDate;
      }

      const intervalsWithSameStartDate = this.priceIntervals.filter(priceInterval =>
        priceInterval.startDate === startDate);
      if (intervalsWithSameStartDate.length) {
        const bookingToDates = intervalsWithSameStartDate.map(e => e.bookTo);
        const formattedBookingToDates = bookingToDates.map(date => this.$moment(date));
        const maxBookFromDate = this.$moment.max(formattedBookingToDates).add(1, 'days');
        this.localPriceInterval.bookFrom = maxBookFromDate.format('YYYY-MM-DD');
      }
    },
    setBookFrom(value) {
      this.localPriceInterval.bookFrom = this.getValue(value);
    },
    setBookTo(value) {
      this.localPriceInterval.bookTo = this.getValue(value);
    },
    setArrivalFrom(value) {
      this.localPriceInterval.arrivalFrom = this.getValue(value);
      this.findAvailableOutboundDays();
    },
    setArrivalTo(value) {
      this.localPriceInterval.arrivalTo = this.getValue(value);
      this.findAvailableOutboundDays();
    },
    findMaxDate(dates) {
      return this.$moment.max(dates).format('YYYY-MM-DD');
    },
    setOutboundDays(days) {
      this.localOutboundDays = days.map(day => ({ day: day.id, direction: tripDirections.OUTBOUND }));
    },
    validateBookingPeriod() {
      if (!this.localPriceInterval.startDate) {
        const intervalsWithSameArrivalPeriod = this.priceIntervals.filter((priceInterval, index) =>
          priceInterval.arrivalFrom === this.localPriceInterval.arrivalFrom &&
          priceInterval.arrivalTo === this.localPriceInterval.arrivalTo && index !== this.editIndex);

        // Find latest bookTo in those periods.
        if (intervalsWithSameArrivalPeriod.length) {
          const bookingToDates = intervalsWithSameArrivalPeriod.map(e => e.bookTo);
          const formattedBookingToDates = bookingToDates.map(date => this.$moment(date));
          const maxBookToDate = this.findMaxDate(formattedBookingToDates);
          if (this.$moment(this.localPriceInterval.bookFrom).isAfter(maxBookToDate)) {
            return true;
          }
          return `Due to same arrival period as another interval,
          the booking period has to start after ${this.$moment(maxBookToDate).format('DD/MM/YYYY')}`;
        }
        return true;
      }
      return true;
    },
    validateArrivalPeriod() {
      if (!this.localPriceInterval.startDate) {
        const intervalsWithSameBookingPeriod = this.priceIntervals.filter((priceInterval, index) =>
          priceInterval.bookFrom === this.localPriceInterval.bookFrom &&
          priceInterval.bookTo === this.localPriceInterval.bookTo && index !== this.editIndex);

        // Find latest arrivalTo
        if (intervalsWithSameBookingPeriod.length) {
          const arrivalToDates = intervalsWithSameBookingPeriod.map(e => e.arrivalTo);
          const formattedArrivalToDates = arrivalToDates.map(date => this.$moment(date));
          const maxArrivalToDate = this.findMaxDate(formattedArrivalToDates);
          if (this.$moment(this.localPriceInterval.arrivalFrom).isAfter(maxArrivalToDate)) {
            return true;
          }
          return `Due to same booking period as another interval,
          the arrival period has to start after ${this.$moment(maxArrivalToDate).format('DD/MM/YYYY')}`;
        }
        return true;
      }
      return true;
    },
    findAvailableOutboundDays() {
      this.availableOutboundDays = [];
      if (this.localPriceInterval.arrivalFrom && this.localPriceInterval.arrivalTo) {
        const amountOfDaysBetween = this.getDaysBetweenDates(this.localPriceInterval.arrivalFrom,
          this.localPriceInterval.arrivalTo);
        const availableDays = amountOfDaysBetween - this.itineraryLength;
        if (availableDays >= 7) {
          this.availableOutboundDays = cloneDeep(this.days);
        } else {
          const dates = [];
          for (let i = 0; i <= availableDays; i++) {
            const date = this.$moment(this.localPriceInterval.arrivalFrom, 'YYYY-MM-DD').add(i, 'days');
            dates.push(+date.format('d'));
          }
          dates.forEach((date) => {
            this.days.forEach((day) => {
              if (date === day.id) {
                this.availableOutboundDays.push(cloneDeep(day));
              }
            });
          });
        }
      }
    },
    getDaysBetweenDates(firstDate, secondDate) {
      const start = this.$moment(firstDate, 'YYYY-MM-DD');
      const end = this.$moment(secondDate, 'YYYY-MM-DD');
      // Plus 1 to include the first day.
      return this.$moment.duration(end.diff(start)).asDays() + 1;
    },
    savePriceInterval() {
      this.localPriceInterval.intervalDays = [...this.localOutboundDays];
      this.localEditDialog = !this.localEditDialog;
      this.$emit('save', this.localPriceInterval);
    },
    close() {
      this.$emit('close');
    },
  },
};
</script>

<template>
  <v-dialog
    v-model="localEditDialog"
    max-width="800px"
    persistent
  >
    <template v-slot:activator="{ on }">
      <v-btn
        v-on="on"
        color="primary"
        class="mb-2"
      >
        Create new interval
      </v-btn>
    </template>
    <v-card>
      <v-card-title>
        <span class="headline">Create/edit price interval - Itinerary is {{itineraryLength}} days</span>
      </v-card-title>

      <v-divider class="ml-3 mr-4"/>

      <v-card-text>
        <v-container grid-list-md>
          <v-layout wrap mb-3>
            <v-flex xs2 mr-5>
              <v-checkbox
                v-model="localPriceInterval.active"
                :label="activeStatus"
                color="success"
                class="activeStatus"
              />
            </v-flex>
            <v-flex xs4 right>
              <v-menu
                v-model="datePickers.startDate"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                min-width="290px"
                full-width
                lazy
                offset-y
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-on="on"
                    :value="localPriceInterval.startDate | moment('DD/MM/YYYY')"
                    prepend-icon="today"
                    label="Start date"
                    clearable
                    readonly
                    @click:clear="setStartDate($event.target.value)"
                  />
                </template>
                <v-date-picker
                  v-model="localPriceInterval.startDate"
                  :min="today"
                  @input="datePickers.startDate = false"
                  @change="setStartDate"
                />
              </v-menu>
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-flex xs6>
              <v-menu
                v-model="datePickers.bookingFrom"
                :close-on-content-click="false"
                :nudge-right="40"
                min-width="290px"
                transition="scale-transition"
                full-width
                lazy
                offset-y
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-on="on"
                    :value="localPriceInterval.bookFrom | moment('DD/MM/YYYY')"
                    :rules="[validateBookingPeriod]"
                    prepend-icon="today"
                    label="Booking from"
                    clearable
                    readonly
                  />
                </template>
                <v-date-picker
                  v-model="localPriceInterval.bookFrom"
                  :min="today"
                  @input="datePickers.bookingFrom = false"
                  @change="setBookFrom"
                />
              </v-menu>
            </v-flex>
            <v-flex xs6>
              <v-menu
                v-model="datePickers.bookingTo"
                :close-on-content-click="false"
                :nudge-right="40"
                min-width="290px"
                transition="scale-transition"
                full-width
                lazy
                offset-y
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-on="on"
                    :value="localPriceInterval.bookTo | moment('DD/MM/YYYY')"
                    prepend-icon="today"
                    label="Booking to"
                    clearable
                    readonly
                  />
                </template>
                <v-date-picker
                  v-model="localPriceInterval.bookTo"
                  :min="localPriceInterval.bookFrom"
                  @input="datePickers.bookingTo = false"
                  @change="setBookTo"
                />
              </v-menu>
            </v-flex>
            <v-flex xs6>
              <v-menu
                v-model="datePickers.arrivalFrom"
                :close-on-content-click="false"
                :nudge-right="40"
                min-width="290px"
                transition="scale-transition"
                full-width
                lazy
                offset-y
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-on="on"
                    :value="localPriceInterval.arrivalFrom | moment('DD/MM/YYYY')"
                    :rules="[validateArrivalPeriod]"
                    prepend-icon="today"
                    label="Arrival from"
                    readonly
                  />
                </template>
                <v-date-picker
                  v-model="localPriceInterval.arrivalFrom"
                  :min="today"
                  @input="datePickers.arrivalFrom = false"
                  @change="setArrivalFrom"
                />
              </v-menu>
            </v-flex>
            <v-flex xs6>
              <v-menu
                v-model="datePickers.arrivalTo"
                :close-on-content-click="false"
                :nudge-right="40"
                lazy
                transition="scale-transition"
                offset-y
                full-width
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-on="on"
                    :value="localPriceInterval.arrivalTo | moment('DD/MM/YYYY')"
                    prepend-icon="today"
                    label="Arrival to"
                    readonly
                  />
                </template>
                <v-date-picker
                  v-model="localPriceInterval.arrivalTo"
                  :min="localPriceInterval.arrivalFrom"
                  @input="datePickers.arrivalTo = false"
                  @change="setArrivalTo"
                />
              </v-menu>
            </v-flex>
          </v-layout>
          <v-layout>
            <v-flex xs8>
              <v-select
                :value="outboundDays"
                :items="availableOutboundDays"
                :disabled="daysDisabled"
                label="Outbound days"
                item-text="label"
                item-value="id"
                multiple
                chips
                persistent-hint
                return-object
                @change="setOutboundDays"
              />
            </v-flex>
          </v-layout>
          <v-layout wrap>
            <v-flex xs8 mr-5>
              <v-checkbox
                v-model="localPriceInterval.bookingFlow"
                label="Booking Flow"
                color="success"
              />
            </v-flex>
          </v-layout>
          <v-layout wrap>
            <v-flex xs8 mr-5>
              <v-checkbox
                v-model="localPriceInterval.guaranteed"
                label="Guaranteed"
                color="success"
              />
            </v-flex>
          </v-layout>
          <v-layout wrap>
            <v-flex xs8 mr-5>
              <v-checkbox
                v-model="localPriceInterval.fixedExchangeRate"
                label="Fixed exchange rate"
                color="success"
              />
            </v-flex>
          </v-layout>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn color="error" @click="close">
          Cancel
        </v-btn>
        <v-btn color="success" @click="savePriceInterval">
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>



// WEBPACK FOOTER //
// src/components/itinerary/itineraryPrices/ItineraryPricesPriceIntervalCreator.vue